import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight, Eye } from 'lucide-react';
import { Button, Card, SectionHeader, Select, Skeleton } from '../../../components/ui';
import type { SubjectRef, WatchFrequency, WatchSubjectType } from '../../../types/monitoring';
import { FREQUENCIES, FREQUENCY_LABEL, timeAgo } from '../format';
import { useEntityWatch, useSignals } from '../useMonitoring';
import { SignalFeed } from './SignalFeed';
import { Notice } from './ui';

export interface EntityMonitoringPanelProps {
  subjectType: WatchSubjectType;
  subjectId: string;
  subjectLabel: string;
  /** How many signals to show before linking out to the full feed */
  limit?: number;
  className?: string;
}

/**
 * The monitoring half of an entity's News tab: whether it is watched, how often,
 * and the signals found for it — with everything else a click away on /monitoring.
 */
export const EntityMonitoringPanel: React.FC<EntityMonitoringPanelProps> = ({
  subjectType,
  subjectId,
  subjectLabel,
  limit = 5,
  className = '',
}) => {
  const watchState = useEntityWatch(subjectType, subjectId);
  const { watch, loading: watchLoading, error: watchError, unavailable, saving, start, setFrequency, stop } = watchState;
  const [showAll, setShowAll] = useState(false);

  const query = useMemo(() => ({ subjectType, subjectId, limit: showAll ? 20 : limit }), [subjectType, subjectId, limit, showAll]);
  const signals = useSignals(query);

  const subject: SubjectRef = { type: subjectType, id: subjectId, label: subjectLabel };
  const feedHref = `/monitoring?tab=signals&subjectType=${subject.type}&subjectId=${subject.id}&subjectLabel=${encodeURIComponent(
    subject.label,
  )}`;
  const noun = subjectType === 'organization' ? 'organization' : 'stakeholder';

  if (unavailable) {
    return (
      <div className={className}>
        <Notice tone="quiet">Monitoring is not available on this server yet — nothing to watch from here.</Notice>
      </div>
    );
  }

  return (
    <div className={`space-y-5 ${className}`}>
      <Card padded className="flex flex-wrap items-center gap-x-4 gap-y-3">
        <span className="inline-flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-neutral-100 text-neutral-500">
          <Eye className="h-4 w-4" strokeWidth={1.75} />
        </span>
        <div className="min-w-0 flex-1">
          {watchLoading ? (
            <Skeleton className="h-4 w-44" />
          ) : watch ? (
            <>
              <p className="text-[13px] text-ink">Watching this {noun}</p>
              <p className="mt-0.5 text-[12px] text-neutral-400">
                {watch.lastScanAt ? `Last scanned ${timeAgo(watch.lastScanAt)}` : 'Not scanned yet'} ·{' '}
                {watch.recentSignals} signal{watch.recentSignals === 1 ? '' : 's'} in 30 days
              </p>
            </>
          ) : (
            <>
              <p className="text-[13px] text-ink">Watch this {noun}</p>
              <p className="mt-0.5 text-[12px] text-neutral-400">Scans for news and raises early warnings.</p>
            </>
          )}
        </div>

        {!watchLoading &&
          (watch ? (
            /* On a phone the controls take a line of their own: squeezed in
               beside the text, the frequency reads as a sliver and Stop sits
               half off the card */
            <div className="flex w-full flex-shrink-0 items-center gap-2 sm:w-auto">
              <Select
                aria-label="Scan frequency"
                className="min-w-0 flex-1 sm:w-[130px] sm:flex-none"
                value={watch.frequency}
                disabled={saving}
                onChange={(event) => setFrequency(event.target.value as WatchFrequency)}
              >
                {FREQUENCIES.map((value) => (
                  <option key={value} value={value}>
                    {FREQUENCY_LABEL[value]}
                  </option>
                ))}
              </Select>
              <Button size="sm" variant="ghost" className="flex-shrink-0" disabled={saving} onClick={stop}>
                Stop
              </Button>
            </div>
          ) : (
            <Button
              size="sm"
              variant="primary"
              className="w-full sm:w-auto"
              loading={saving}
              onClick={() => start('weekly')}
            >
              Watch
            </Button>
          ))}

        {watchError && (
          <Notice className="w-full" tone="error">
            {watchError}
          </Notice>
        )}
      </Card>

      <section>
        <SectionHeader
          title="Signals"
          aside={
            signals.total > 0 ? (
              <Link
                to={feedHref}
                className="inline-flex items-center gap-0.5 text-[12px] text-neutral-500 transition-colors hover:text-ink"
              >
                All {signals.total}
                <ArrowUpRight className="h-3.5 w-3.5" strokeWidth={1.75} />
              </Link>
            ) : undefined
          }
        />
        <SignalFeed
          signals={signals.items}
          loading={signals.loading}
          loadingMore={signals.loadingMore}
          hasMore={signals.hasMore}
          busyId={signals.busyId}
          showSubject={false}
          onStatus={signals.setStatus}
          onLoadMore={() => (showAll ? signals.loadMore() : setShowAll(true))}
          notice={signals.error ? <Notice tone="error">{signals.error}</Notice> : undefined}
          emptyTitle={watch ? 'No signals found yet — the next scan may turn some up' : 'Not watched, so nothing is being collected'}
          emptyAction={
            watch ? undefined : (
              <Button size="sm" variant="secondary" loading={saving} onClick={() => start('weekly')}>
                Watch this {noun}
              </Button>
            )
          }
        />
      </section>
    </div>
  );
};
